// MaterializationPreview — shows the first 5 rows after coercion.
// Per spec §8: show coerced first 5 rows with per-property type validity.
// Errors surfaced inline as text-destructive with the raw value in font-mono.

import { coerce } from "@/lib/ontology/coerce";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Mapping, PropertyType, ObjectType } from "@/types/ontology";

export interface MaterializationPreviewProps {
  rows: Record<string, string>[];
  headers: string[];
  mapping: Mapping;
  propertyTypes: PropertyType[];
  objectType: ObjectType;
}

export function MaterializationPreview({
  rows,
  headers: _headers,
  mapping,
  propertyTypes,
  objectType,
}: MaterializationPreviewProps) {
  // Determine which properties are mapped (column_map: { prop_api_name → column_name }).
  const mappedProps = propertyTypes.filter((p) => mapping.column_map[p.api_name]);

  // Coerce and collect per-row, per-property results.
  const coercedRows = rows.map((rawRow) => {
    const cells: Record<string, { ok: boolean; value: unknown; error?: string; raw: string }> = {};
    for (const prop of mappedProps) {
      const colName = mapping.column_map[prop.api_name];
      const raw = rawRow[colName] ?? "";
      const result = coerce(raw, prop);
      if (result.ok) {
        cells[prop.api_name] = { ok: true, value: result.value, raw };
      } else {
        cells[prop.api_name] = { ok: false, value: null, error: result.error, raw };
      }
    }
    return cells;
  });

  const errorCount = coercedRows.reduce(
    (acc, row) => acc + Object.values(row).filter((c) => !c.ok).length,
    0
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Preview — first {rows.length} rows</p>
          <p className="text-xs text-muted-foreground">
            Object Type: <span className="font-mono">{objectType.api_name}</span>
          </p>
        </div>
        {errorCount > 0 ? (
          <Badge variant="destructive">
            {errorCount} coercion error{errorCount > 1 ? "s" : ""}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-success/50 text-success">
            All rows valid
          </Badge>
        )}
      </div>

      <Separator />

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-xs w-8">#</TableHead>
              {mappedProps.map((prop) => (
                <TableHead key={prop.api_name} className="text-xs">
                  <div className="flex items-center gap-1">
                    <span className="font-mono">{prop.api_name}</span>
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1 py-0 h-4 font-normal flex-shrink-0"
                    >
                      {prop.kind}
                    </Badge>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {coercedRows.map((row, rowIdx) => (
              <TableRow key={rowIdx} className="hover:bg-muted/40">
                <TableCell className="text-xs text-muted-foreground">{rowIdx + 1}</TableCell>
                {mappedProps.map((prop) => {
                  const cell = row[prop.api_name];
                  if (!cell) {
                    return (
                      <TableCell key={prop.api_name} className="text-xs text-muted-foreground">
                        —
                      </TableCell>
                    );
                  }

                  if (!cell.ok) {
                    return (
                      <TableCell key={prop.api_name} className="text-xs">
                        <div>
                          <span className="text-destructive">{cell.error}</span>
                          <span className="ml-1 font-mono text-destructive/70">({cell.raw})</span>
                        </div>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={prop.api_name} className="text-xs font-mono">
                      {formatValue(cell.value)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {mappedProps.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-4">
          No columns mapped — go back and map at least one column.
        </p>
      )}
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}
